/// <reference path="../../../../types/cordova-plugins.d.ts" />

import {
  Box,
  Button,
  FormControl,
  FormLabel,
  TextField,
  Typography,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  Attachments,
  FileComponentProps,
} from "../../../core/models/managements/managements.model";
import theme from "../../../muiTheme";
import "./ManagementsUploadFile.scss";
import React from "react";
import pdfImg from "../../../assets/images/pdf.svg";
import pngImg from "../../../assets/images/png.svg";
import jpgImg from "../../../assets/images/jpg.svg";
import { downloadDocument } from "../../../core/services/managements/managements.service";
import LoadingComponentComponent from "../../UI/LoadingComponentComponent/LoadingComponentComponent";
interface CustomWindow extends Window {
  PreviewAnyFile?: {
    previewPath: (
      successCallback: (win: any) => void,
      errorCallback: (error: any) => void,
      path: string,
      options: { name: string }
    ) => void;
  };
}

declare let window: CustomWindow;
const ManagementUploadFile: React.FC<FileComponentProps> = ({
  onFileUpload,
  downloadDocumentType,
  downloadButtonText,
  multipleFiles,
  title,
  isRequired,
  inputFileInstructions,
  optionalCustomMessage,
  onFileRemove,
  adjunstFileButtonText,
  ...props
}) => {
  const { t } = useTranslation();
  const [errorOnUpload, setErrorOnUpload] = useState(false);
  const [attachments, setAttachments] = useState<Attachments[]>([
    { id: 1, value: "", fileInfo: {} },
  ]);
  const [loading, setLoading] = useState(false);

  const inputId = (title || "default-title").toLowerCase().replace(/ /g, "-");

  const handleDownload = async () => {
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (downloadDocumentType) {
      try {
        const response = await downloadDocument(downloadDocumentType);
        const url = response.documentData.documentContent;
        const filename = response.documentMetadata.name || "download.pdf";

        if (isMobile && typeof cordova !== "undefined") {
          if (isIOS && cordova.InAppBrowser) {
            cordova.InAppBrowser.open(url, "_system");
          } else {
            window.PreviewAnyFile?.previewPath(
              (win: any) => console.log("open status", win),
              (error: any) => console.error("open failed", error),
              url,
              { name: filename }
            );
          }
        } else {
          const result = await fetch(url);
          const blob = await result.blob();
          const blobUrl = URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = filename;

          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          URL.revokeObjectURL(blobUrl);
        }
      } catch (error) {
        console.error("Error downloading the file", error);
      }
    }
  };

  const handleAddAttachment = () => {
    setAttachments((prevAttachments) => [
      ...prevAttachments,
      { id: prevAttachments.length + 1, value: "", fileInfo: {} },
    ]);
  };

  const [fileLoad, setFileLoad] = useState<File | null>(null);

  const handleFileChange =
    (id: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setLoading(true);

      const file = event.target.files && event.target.files[0];
      setFileLoad(file);

      setTimeout(() => {
        if (file) {
          const MAX_FILE_SIZE = 4 * 1024 * 1024;

          if (file.size <= MAX_FILE_SIZE) {
            if (onFileUpload) {
              onFileUpload(id, file);
              setLoading(false);
            }

            const fileExtension = file.name.split(".").pop()?.toLowerCase();

            setAttachments((prevAttachments) =>
              prevAttachments.map((att) =>
                att.id === id
                  ? {
                      ...att,
                      fileInfo: {
                        name: file.name,
                        size: Math.round(file.size / 1024),
                        date: new Date(),
                        extension: fileExtension,
                      },
                    }
                  : att
              )
            );
          } else {
            setErrorOnUpload(true);
            setLoading(false);
          }
        }
      }, 1000);
    };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const [removedFileId, setRemovedFileId] = useState<number | null>(null);
  useEffect(() => {
    if (removedFileId !== null && onFileRemove) {
      onFileRemove(removedFileId);
      setRemovedFileId(null);
    }
  }, [removedFileId, onFileRemove]);

  const handleRemoveFile = (id: number) => {
    setAttachments((prevAttachments) => {
      const updatedAttachments = prevAttachments.filter((att) => att.id !== id);

      setRemovedFileId(id);

      if (updatedAttachments.length === 0) {
        return [...updatedAttachments, { id: 1, value: "", fileInfo: {} }];
      }

      return updatedAttachments;
    });
  };

  return (
    <FormControl
      required={isRequired}
      sx={{
        alignItems: "flex-start",
        display: "flex",
        gap: "1rem",
      }}
    >
      <Box
        component="div"
        display="flex"
        flexDirection="row"
        flex={1}
        width={"100%"}
        justifyContent="space-between"
      >
        {title && (
          <FormLabel htmlFor={inputId}>
            <Typography fontWeight={600}>
              {title}

              <Typography variant="body2" component="span">
                {isRequired ? (
                  <span style={{ color: "red" }}> *</span>
                ) : optionalCustomMessage ? (
                  <> - {optionalCustomMessage}</>
                ) : (
                  <> - {t("Opcional")}</>
                )}
              </Typography>
            </Typography>
          </FormLabel>
        )}
      </Box>
      {attachments.map(({ id, fileInfo }) => (
        <React.Fragment key={id}>
          {fileInfo?.name ? (
            <Box
              bgcolor={theme.palette.grey[100]}
              component="div"
              display="flex"
              flexDirection="row"
              flex={1}
              gap={"10px"}
              width={1}
              borderRadius={"4px"}
              border={"1px solid"}
              borderColor={theme.palette.grey[300]}
            >
              <Stack
                direction={"row"}
                justifyContent="space-between"
                width="100%"
                padding={"1rem 1.5rem"}
              >
                {fileInfo.extension === "png" ? (
                  <img src={pngImg} alt="PNG" aria-hidden="true" />
                ) : fileInfo.extension === "jpg" ||
                  fileInfo.extension === "jpeg" ? (
                  <img src={jpgImg} alt="JPG" aria-hidden="true" />
                ) : (
                  <img src={pdfImg} alt="PDF" aria-hidden="true" />
                )}

                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  flex={1}
                  paddingLeft="10px"
                >
                  <Typography variant="body2" component="p">
                    {fileInfo.name}
                  </Typography>
                  <Typography variant="caption" component="span">
                    {fileInfo.size}KB .{" "}
                    {fileInfo.date ? formatDate(fileInfo.date) : ""}
                  </Typography>
                </Box>

                <Button
                  onClick={() => handleRemoveFile(id)}
                  aria-label={t("Gestiones.Eliminar archivo")}
                >
                  <Typography fontSize={"24px"}>
                    <span className="t-icon-trash" />
                  </Typography>
                </Button>
              </Stack>
            </Box>
          ) : loading ? (
            <Box
              bgcolor={theme.palette.grey[100]}
              component="div"
              display="flex"
              flexDirection="row"
              flex={1}
              gap={"10px"}
              width={1}
              borderRadius={"4px"}
              border={"1px solid"}
              borderColor={theme.palette.grey[300]}
            >
              <Stack
                direction={"row"}
                justifyContent="space-between"
                width="100%"
              >
                <LoadingComponentComponent
                  isLoading={true}
                  backgroundColor={"transparent"}
                  position={"relative"}
                  height={"4rem"}
                  width={"4rem"}
                />
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  margin={"auto"}
                  flex={1}
                >
                  <Typography variant="body2" component="p">
                    {fileLoad?.name}
                  </Typography>
                  <Typography variant="caption" component="span">
                    {fileLoad?.size}KB . {t("Cargando")}...
                  </Typography>
                </Box>

                <Button onClick={() => handleRemoveFile(id)}>
                  <span className="t-icon-close" />
                </Button>
              </Stack>
            </Box>
          ) : (
            <>
              <Box
                component="div"
                display="flex"
                flexDirection="row"
                flex={1}
                width={"100%"}
              >
                <TextField
                  variant="standard"
                  className={`attached-file-input ${
                    errorOnUpload ? "has-error" : ""
                  }`}
                  type="text"
                  disabled
                  sx={{ width: "100%" }}
                  error={errorOnUpload}
                  InputProps={{
                    disableUnderline: true,
                    endAdornment: (
                      <Button
                        component="label"
                        className="attached-file-button"
                        sx={{
                          order: -1,
                          width: "100%",
                          gap: "0.75rem",
                          padding: "1.5rem",
                          justifyContent: "start",
                        }}
                      >
                        <input
                          id={inputId}
                          name={inputId}
                          type="file"
                          hidden
                          accept=".pdf, image/png, image/jpeg"
                          onChange={handleFileChange(id)}
                          style={{ flexGrow: 1 }}
                        />
                        <span className="t-icon-Paperclip" />
                        <Typography
                          color={theme.palette.grey[900]}
                          fontWeight={600}
                        >
                          {adjunstFileButtonText ?? t("Adjuntar Anexo")}
                        </Typography>
                      </Button>
                    ),
                  }}
                />
              </Box>
              {errorOnUpload ? (
                <Typography
                  variant="caption"
                  component="p"
                  color={theme.palette.error.main}
                >
                  <span className="t-icon-info" aria-hidden="true"></span>{" "}
                  {t("Gestiones.Error carga de archivo")}
                </Typography>
              ) : (
                inputFileInstructions !== false && (
                  <Typography variant="caption" component="p">
                    {t("Gestiones.Formatos aceptados")}
                  </Typography>
                )
              )}
            </>
          )}
        </React.Fragment>
      ))}
      {downloadDocumentType && (
        <Button
          color="primary"
          onClick={handleDownload}
          sx={{ padding: "0", height: "44px" }}
        >
          <span
            className="t-icon-download"
            aria-hidden="true"
            style={{ paddingRight: "10px" }}
          ></span>
          {downloadButtonText}
        </Button>
      )}
      {multipleFiles && (
        <FormControl sx={{ alignItems: "start" }}>
          <Button
            component="label"
            onClick={handleAddAttachment}
            sx={{ padding: 0 }}
          >
            <span className="t-icon-plus" style={{ paddingRight: "0.5rem" }} />
            {t("Añadir adjunto")}
          </Button>
        </FormControl>
      )}
    </FormControl>
  );
};

export default ManagementUploadFile;
